import React from 'react';
import { useTranslation } from 'react-i18next';
import { BdsIcon } from '@/core/constants/webComponents';
import './EditAdvancedSettingsModal.scss';

export interface AdvancedSettingsState {
  antiSpam: boolean;
  autoSentiment: boolean;
  includeNeutral: boolean;
  processRetweets: boolean;
  startDate: string;
  panelName: string;
}

interface EditAdvancedSettingsModalProps {
  isOpen: boolean;
  settings: AdvancedSettingsState;
  onChange: (nextSettings: AdvancedSettingsState) => void;
  onFinish?: () => void;
  onClose: () => void;
}

type ToggleKey = 'antiSpam' | 'autoSentiment' | 'includeNeutral' | 'processRetweets';

const getOptionClassName = (isActive: boolean): string =>
  `new-setup__advanced-modal-option ${isActive ? 'new-setup__advanced-modal-option--active' : 'new-setup__advanced-modal-option--inactive'}`;

const getSwitchClassName = (isActive: boolean): string =>
  `new-setup__advanced-modal-switch ${isActive ? 'new-setup__advanced-modal-switch--active' : 'new-setup__advanced-modal-switch--inactive'}`;

export const EditAdvancedSettingsModal: React.FC<EditAdvancedSettingsModalProps> = ({
  isOpen,
  settings,
  onChange,
  onFinish,
  onClose,
}) => {
  const { t } = useTranslation();

  if (!isOpen) {
    return null;
  }

  const handleFinish = () => {
    onFinish?.();
    onClose();
  };

  const toggleSetting = (key: ToggleKey) => {
    onChange({
      ...settings,
      [key]: !settings[key],
    });
  };

  const updateStartDate = (startDate: string) => {
    onChange({
      ...settings,
      startDate,
    });
  };

  const updatePanelName = (panelName: string) => {
    onChange({
      ...settings,
      panelName,
    });
  };

  return (
    <div className="new-setup__modal-overlay">
      <dialog open className="new-setup__advanced-modal" aria-labelledby="new-setup-advanced-modal-title">
        <header className="new-setup__advanced-modal-header">
          <button
            type="button"
            className="new-setup__advanced-modal-close"
            onClick={onClose}
            aria-label={t('newSetup.advancedModal.close')}
          >
            <BdsIcon theme="outline" name="close" size="medium" aria-hidden="true" />
          </button>

          <h2 id="new-setup-advanced-modal-title" className="new-setup__advanced-modal-title">
            {t('newSetup.advancedModal.title')}
          </h2>
          <p className="new-setup__advanced-modal-subtitle">{t('newSetup.advancedModal.subtitle')}</p>
        </header>

        <div className="new-setup__advanced-modal-content">
          <div className="new-setup__advanced-modal-divider" />

          <div className="new-setup__advanced-modal-copy">
            <h3 className="new-setup__advanced-modal-copy-title">{t('newSetup.advancedModal.sectionTitle')}</h3>
            <p className="new-setup__advanced-modal-copy-text">{t('newSetup.advancedModal.sectionDescription')}</p>
          </div>

          <div className="new-setup__advanced-modal-options">
            <button
              type="button"
              className={getOptionClassName(settings.antiSpam)}
              onClick={() => toggleSetting('antiSpam')}
              aria-pressed={settings.antiSpam}
            >
              <div className="new-setup__advanced-modal-option-texts">
                <p className="new-setup__advanced-modal-option-title">{t('newSetup.advancedModal.options.antiSpam.title')}</p>
                <p className="new-setup__advanced-modal-option-description">
                  {t('newSetup.advancedModal.options.antiSpam.description')}
                </p>
              </div>
              <span className={getSwitchClassName(settings.antiSpam)} aria-hidden="true">
                <span className="new-setup__advanced-modal-switch-knob" />
              </span>
            </button>

            <button
              type="button"
              className={getOptionClassName(settings.autoSentiment)}
              onClick={() => toggleSetting('autoSentiment')}
              aria-pressed={settings.autoSentiment}
            >
              <div className="new-setup__advanced-modal-option-texts">
                <p className="new-setup__advanced-modal-option-title">{t('newSetup.advancedModal.options.autoSentiment.title')}</p>
                <p className="new-setup__advanced-modal-option-description">
                  {t('newSetup.advancedModal.options.autoSentiment.description')}
                </p>
              </div>
              <span className={getSwitchClassName(settings.autoSentiment)} aria-hidden="true">
                <span className="new-setup__advanced-modal-switch-knob" />
              </span>
            </button>

            <button
              type="button"
              className={getOptionClassName(settings.includeNeutral)}
              onClick={() => toggleSetting('includeNeutral')}
              aria-pressed={settings.includeNeutral}
            >
              <div className="new-setup__advanced-modal-option-texts">
                <p className="new-setup__advanced-modal-option-title">{t('newSetup.advancedModal.options.includeNeutral.title')}</p>
                <p className="new-setup__advanced-modal-option-description">
                  {t('newSetup.advancedModal.options.includeNeutral.description')}
                </p>
              </div>
              <span className={getSwitchClassName(settings.includeNeutral)} aria-hidden="true">
                <span className="new-setup__advanced-modal-switch-knob" />
              </span>
            </button>

            <button
              type="button"
              className={getOptionClassName(settings.processRetweets)}
              onClick={() => toggleSetting('processRetweets')}
              aria-pressed={settings.processRetweets}
            >
              <div className="new-setup__advanced-modal-option-texts">
                <p className="new-setup__advanced-modal-option-title">{t('newSetup.advancedModal.options.processRetweets.title')}</p>
                <p className="new-setup__advanced-modal-option-description">
                  {t('newSetup.advancedModal.options.processRetweets.description')}
                </p>
              </div>
              <span className={getSwitchClassName(settings.processRetweets)} aria-hidden="true">
                <span className="new-setup__advanced-modal-switch-knob" />
              </span>
            </button>
          </div>

          <div className="new-setup__advanced-modal-divider" />

          <div className="new-setup__advanced-modal-field-group">
            <label className="new-setup__advanced-modal-field-label" htmlFor="new-setup-advanced-start-date">
              {t('newSetup.advancedModal.startDateLabel')}
            </label>
            <input
              id="new-setup-advanced-start-date"
              className="new-setup__advanced-modal-input"
              value={settings.startDate}
              onChange={(event) => updateStartDate(event.target.value)}
            />
            <div className="new-setup__advanced-modal-info">
              <BdsIcon name="info" size="x-small" theme="outline" aria-hidden="true" />
              <p className="new-setup__advanced-modal-info-text">{t('newSetup.advancedModal.startDateInfo')}</p>
            </div>
          </div>

          <div className="new-setup__advanced-modal-field-group">
            <label className="new-setup__advanced-modal-field-label" htmlFor="new-setup-advanced-panel-name">
              {t('newSetup.advancedModal.panelNameLabel')}
            </label>
            <input
              id="new-setup-advanced-panel-name"
              className="new-setup__advanced-modal-input"
              value={settings.panelName}
              onChange={(event) => updatePanelName(event.target.value)}
            />
          </div>
        </div>

        <footer className="new-setup__advanced-modal-footer">
          <button type="button" className="new-setup__advanced-modal-confirm" onClick={handleFinish}>
            {t('newSetup.advancedModal.finish')}
          </button>
        </footer>
      </dialog>
    </div>
  );
};
